import { useTranslation } from "react-i18next";

import { MarkdownView } from "@/components/shared/MarkdownView";
import {
  buildFlatWizardPricingBreakdown,
  buildPerFileWizardPricingBreakdown,
  formatCatalogAmountMinor,
  type CertificatePricingCatalogItem,
  type CertificateWizardPricingBreakdownData,
  type CertificateWizardPricingDetailLine,
} from "@/services/certificatePricingCatalogService";

function lineLabel(
  line: CertificateWizardPricingDetailLine,
  t: (key: string, opts?: Record<string, string | number>) => string
): string {
  switch (line.type) {
    case "regulated_base":
      return t("requests.user.pricingBreakdown.regulatedBase");
    case "income_base":
      return t("requests.user.pricingBreakdown.incomeCertificateBase");
    case "fixed_component":
      return line.label || t("requests.user.pricingBreakdown.fixedAmount");
    case "ur_component": {
      const urTypeLabel = line.urType === "arancel"
        ? t("requests.user.pricingBreakdown.urTypeArancel")
        : t("requests.user.pricingBreakdown.urTypeDgi");
      return t("requests.user.pricingBreakdown.urUnitsWithType", { units: line.urUnits, urType: urTypeLabel });
    }
    case "service_fee":
      return t("requests.user.pricingBreakdown.serviceFee");
    case "certificate_service":
      return t("requests.user.pricingBreakdown.certificateService");
    case "document_storage":
      return t("requests.user.pricingBreakdown.documentStorage", { count: line.documentCount });
    case "income_service_margin":
    case "income_margin_and_storage":
      return t("requests.user.pricingBreakdown.serviceCost");
    case "professional_stamp":
      return t("requests.user.pricingBreakdown.professionalStamp");
    default: {
      const _exhaustive: never = line;
      return _exhaustive;
    }
  }
}

function hasExpandablePricingRows(breakdown: CertificateWizardPricingBreakdownData | null): boolean {
  if (breakdown == null) return false;
  return (
    breakdown.detailLines.length > 0 ||
    (breakdown.deliveryIncludedMinor != null && breakdown.deliveryIncludedMinor > 0)
  );
}

export function CertificateWizardPricingBreakdown({
  catalogItem,
  variant,
  billableFileCount,
  totalChargeLabel,
  fulfillmentMode,
}: {
  catalogItem: CertificatePricingCatalogItem | undefined;
  variant: "flat" | "per_file";
  billableFileCount?: number;
  totalChargeLabel: string | null;
  fulfillmentMode?: string;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();

  if (!totalChargeLabel) {
    return null;
  }

  const breakdown: CertificateWizardPricingBreakdownData | null =
    variant === "flat"
      ? buildFlatWizardPricingBreakdown(catalogItem, fulfillmentMode)
      : buildPerFileWizardPricingBreakdown(
          catalogItem,
          billableFileCount ?? 0,
          fulfillmentMode,
        );

  const description = catalogItem?.description?.trim() ?? "";
  const hasDescription = description.length > 0;

  const showPricingRows = hasExpandablePricingRows(breakdown);
  const totalLabel = t("requests.user.pricingBreakdown.totalWithVatIncluded");

  const priceInner = (
    <>
      {showPricingRows && breakdown ? (
        <div className="space-y-3">
          {breakdown.detailLines.map((line, index) => (
            <div
              key={`${line.type}-${index}`}
              className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
            >
              <span className="shrink-0 text-sm font-medium text-foreground sm:max-w-[70%]">
                {lineLabel(line, t)}
              </span>
              <span className="text-sm tabular-nums text-muted-foreground sm:text-right">
                {formatCatalogAmountMinor(line.amountMinor, breakdown.currency, i18n.language)}
              </span>
            </div>
          ))}
          {breakdown.deliveryIncludedMinor != null && breakdown.deliveryIncludedMinor > 0 ? (
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
              <span className="shrink-0 text-sm font-medium text-foreground sm:max-w-[70%]">
                {t("requests.user.pricingBreakdown.deliveryIncluded")}
              </span>
              <span className="text-sm tabular-nums text-muted-foreground sm:text-right">
                {formatCatalogAmountMinor(
                  breakdown.deliveryIncludedMinor,
                  breakdown.currency,
                  i18n.language,
                )}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={showPricingRows && breakdown ? "border-t border-border pt-3" : ""}>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <span className="text-sm font-semibold text-foreground">{totalLabel}</span>
          <span className="text-base font-semibold tabular-nums text-foreground">
            {totalChargeLabel}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-3">
      {hasDescription ? (
        <div className="wizard-summary-panel p-4">
          <MarkdownView markdown={description} />
        </div>
      ) : null}

      <div className="wizard-summary-panel p-4">
        {showPricingRows ? (
          <div className="space-y-3">{priceInner}</div>
        ) : (
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
            <span className="text-sm font-semibold text-foreground">{totalLabel}</span>
            <span className="text-base font-semibold tabular-nums text-foreground">{totalChargeLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}
